import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BxSpinnerModule } from '@buildxact/ui-components/bx-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { DatatableComponent, NgxDatatableModule } from '@siemens/ngx-datatable';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, combineLatest, debounceTime, map, switchMap } from 'rxjs';
import { JobVariationFilterInput, SearchPurchaseOrders, Uuid } from '../../../../../../../graphql';
import { DynamicCurrencyPipeModule } from '../../../../../../core/pipes';
import { BxLogger } from '../../../../../../core/services/bx-logger.service';
import { JobsV2Service } from '../../../../../../core/services/jobsV2.service';
import { FeatureService } from '../../../../../../core/services/permission';
import { SearchPurchaseOrdersVariables } from '../../../../purchase-orders/domain/api/queries/SearchPurchaseOrders.generated';
import { JobVariationsVariables } from '../../../variations/domain/api/queries/GetJobVariations.generated';
import { JobVariation, JobVariationsV2Service } from '../../../variations/job-variationsV2.service';

export interface AddToExistingResult {
    id: Uuid;
    type: 'Order' | 'Variation';
}

@Component({
    selector: 'app-job-actual-add-to-existing',
    imports: [CommonModule, FormsModule, NgxDatatableModule, DynamicCurrencyPipeModule, TranslateModule, BxSpinnerModule],
    templateUrl: './job-actual-add-to-existing.component.html',
    styleUrl: './job-actual-add-to-existing.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobActualAddToExistingComponent {
    hasCompactJobs = inject(FeatureService).isAvailable('compact-jobs');
    @ViewChild('ordersTable') ordersTable?: DatatableComponent;

    // <Set via BsModelRef>
    jobId?: Uuid;
    canCreateOrders = false;
    canCreateVariations = false;
    // </Set via BsModelRef>

    selection = new EventEmitter<AddToExistingResult>();

    #jobsV2Service = inject(JobsV2Service);
    #jobVariationsV2Service = inject(JobVariationsV2Service);
    #logger = inject(BxLogger);

    isLoading = false;

    search = '';
    searchText$ = new BehaviorSubject('');

    readonly pageSize = 5;

    ordersPageNum$ = new BehaviorSubject(0);
    orders$ = combineLatest([this.searchText$, this.ordersPageNum$]).pipe(
        debounceTime(500),
        switchMap(([searchText, pageNum]) => (this.canCreateOrders ? this.#searchOrders(searchText, pageNum) : []))
    );
    variationsPageNum$ = new BehaviorSubject(0);
    variations$ = combineLatest([this.searchText$, this.variationsPageNum$]).pipe(
        debounceTime(500),
        switchMap(([searchText, pageNum]) => (this.canCreateVariations ? this.#searchVariations(searchText, pageNum) : []))
    );

    totalOrders = 0;
    totalVariations = 0;

    constructor(private bsModalRef: BsModalRef) {}

    cancel(): void {
        this.bsModalRef.hide();
    }

    #searchOrders(searchText: string, pageNum: number) {
        const request: SearchPurchaseOrdersVariables['request'] = {
            jobId: this.jobId,
            statuses: 'unsent',
            search: searchText || '',
            orderBy: 'orderDate',
            orderByDesc: true,
            rowOffset: pageNum * this.pageSize,
            rowFetch: this.pageSize,
        };

        if (this.hasCompactJobs) request.orderType = 'purchase';

        return this.#jobsV2Service.searchPurchaseOrders(request).pipe(
            map((result) => {
                const res = result.data;

                if (!res) {
                    this.#logger.error('Something went wrong searching for orders.');
                    return [];
                }
                this.totalOrders = res.searchPurchaseOrders.inlineCount;
                return res.searchPurchaseOrders.results;
            })
        );
    }

    #searchVariations(searchText: string, pageNum: number) {
        const request: JobVariationsVariables = {
            order: { createdAt: 'DESC' },
            skip: pageNum * this.pageSize,
            take: this.pageSize,
            where: { iD_Job: { eq: this.jobId }, isDeleted: { eq: false }, status: { eq: 'PENDING' } },
        };

        if (searchText) {
            const or: JobVariationFilterInput[] = [{ description: { contains: searchText } }];
            if (searchText.match(/^[0-9]+$/)) or.push({ variationNo: { eq: +searchText } });

            request.where!.or = or;
        }

        return this.#jobVariationsV2Service.getVariations(request).pipe(
            map((result) => {
                const res = result.data.jobVariations;
                if (!res) {
                    this.#logger.error('Something went wrong searching for variations.');
                    return [];
                }
                this.totalVariations = res.totalCount;
                return res.items ?? [];
            })
        );
    }

    selectOrder(order: SearchPurchaseOrders) {
        this.submit(order.iD_PurchaseOrder, 'Order');
    }

    selectVariation(variation: JobVariation) {
        this.submit(variation.iD_Variation, 'Variation');
    }

    submit(id: Uuid, type: AddToExistingResult['type']) {
        this.selection.emit({ id, type });
        this.bsModalRef.hide();
    }
}
